import { createContext, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useBranding } from '@/hooks/useBranding';
import type { Branding } from '@/types/branding';
import { defaultBranding } from '@/types/branding';

const BrandingContext = createContext<Branding>(defaultBranding);

// eslint-disable-next-line react-refresh/only-export-components
export function useBrandingContext() {
  return useContext(BrandingContext);
}

function updateFavicon(url: string | undefined) {
  const id = 'brand-favicon';
  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (!url) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.id = id;
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { data: branding } = useBranding();

  useEffect(() => {
    if (!branding) return;
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', branding.primary_color || defaultBranding.primary_color);
  }, [branding]);

  useEffect(() => {
    updateFavicon(branding?.favicon_url);
  }, [branding?.favicon_url]);

  return (
    <BrandingContext.Provider value={branding || defaultBranding}>
      {children}
    </BrandingContext.Provider>
  );
}
